/**
 * SearchButton Component
 *
 * A reusable button that, when clicked, expands to reveal a text input
 * inline next to it. Supports a disabled state with a "coming soon" tooltip.
 * Used across multiple pages (journal articles, surveys, etc.).
 */

import { useState, useRef, useEffect } from "react"
import { Search, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

/** Props for the SearchButton component. */
interface SearchButtonProps {
  /** The current committed search query value (controlled by parent). */
  value: string
  /** Callback fired when the user submits the search (Enter key or Submit button click). */
  onChange: (value: string) => void
  /** Placeholder text for the input field. Defaults to "Search in all columns". */
  placeholder?: string
  /** Whether the search button is disabled. Disabled state shows a tooltip. */
  disabled?: boolean
  /** Optional additional className for the wrapper element. */
  className?: string
  /**
   * When true, the component renders a persistent, always-visible search input
   * instead of a collapsible button. Use this to make search prominent (e.g. in
   * a page header). The input never collapses; the X clears the current query.
   * Defaults to false, preserving the collapsible button behaviour.
   */
  alwaysOpen?: boolean
}

/**
 * A search button that expands into an inline text input when clicked.
 *
 * When collapsed, shows a "Search" button. When expanded, shows the input
 * to the left and a "Submit" button to the right. The search query is only
 * submitted to the parent when the user presses Enter or clicks Submit.
 * Clicking the X icon or pressing Escape collapses the input and clears the query.
 *
 * When disabled, the button is non-interactive and displays a tooltip
 * reading "This feature coming soon!" on hover.
 *
 * @param props - Component props.
 * @param props.value - The current committed search query string.
 * @param props.onChange - Handler called only on submit (Enter or Submit click).
 * @param props.placeholder - Placeholder text for the input. Defaults to "Search in all columns".
 * @param props.disabled - Whether the component is disabled.
 * @param props.className - Optional additional CSS classes for the wrapper.
 * @param props.alwaysOpen - When true, render a persistent (non-collapsing) input.
 * @returns The rendered search button/input element.
 */
export default function SearchButton({
  value,
  onChange,
  placeholder = "Search in all columns",
  disabled = false,
  className,
  alwaysOpen = false,
}: SearchButtonProps): React.JSX.Element {
  // Whether the input is currently expanded/visible
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  // Internal draft value — tracks what the user is typing before submitting
  const [draft, setDraft] = useState<string>(value)

  // Ref for the input element to auto-focus when expanded
  const inputRef = useRef<HTMLInputElement>(null)

  // Ref for the expanded container to detect clicks outside
  const containerRef = useRef<HTMLDivElement>(null)

  // Sync draft with external value when it changes (e.g. parent clears it)
  useEffect(() => {
    setDraft(value)
  }, [value])

  // Auto-focus the input when it becomes visible
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isExpanded])

  // Close the expanded input when the user clicks outside the component
  useEffect(() => {
    if (!isExpanded) return

    /**
     * Handles mousedown events outside the container to collapse the input.
     *
     * @param e - The mouse event.
     */
    const handleClickOutside = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsExpanded(false)
        setDraft(value)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isExpanded, value])

  /**
   * Expand the search input.
   */
  const handleOpen = (): void => {
    setIsExpanded(true)
  }

  /**
   * Submit the current draft value to the parent and collapse back to the button.
   */
  const handleSubmit = (): void => {
    onChange(draft)
    setIsExpanded(false)
  }

  /**
   * Collapse the search input and clear the query.
   */
  const handleClose = (): void => {
    setIsExpanded(false)
    setDraft("")
    onChange("")
  }

  /**
   * Handle keyboard events on the input.
   * - Enter submits the search.
   * - Escape collapses and clears.
   *
   * @param e - The keyboard event.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleSubmit()
    } else if (e.key === "Escape") {
      handleClose()
    }
  }

  // Always-open state — a persistent, prominent input that never collapses.
  // Rendered when `alwaysOpen` is true, used e.g. in a page header. The input
  // stays visible at all times; the X clears the committed query, and the
  // Submit button / Enter key commits the current draft to the parent.
  if (alwaysOpen) {
    return (
      <div className={cn("flex items-center", className)}>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            aria-label={placeholder}
            disabled={disabled}
            className="h-9 w-64 pl-9 pr-8 rounded-r-none border-r-0"
          />
          {draft && (
            <button
              type="button"
              onClick={handleClose}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={handleSubmit}
          disabled={disabled}
          className="h-9 rounded-l-none"
        >
          Search
        </Button>
      </div>
    )
  }

  // Disabled state — render button with tooltip
  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn("inline-flex", className)}>
            <Button variant="outline" size="sm" disabled>
              <Search className="size-4 mr-1.5" />
              Search
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          This feature coming soon!
        </TooltipContent>
      </Tooltip>
    )
  }

  // Expanded state — show input to the left of the submit button
  if (isExpanded) {
    return (
      <div ref={containerRef} className={cn("flex items-center", className)}>
        <div className="relative">
          <Input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="h-8 w-56 pr-8 rounded-r-none border-r-0"
          />
          {draft && (
            <button
              type="button"
              onClick={handleClose}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={handleSubmit} className="rounded-l-none">
          Submit
        </Button>
      </div>
    )
  }

  // Collapsed state — just the button
  return (
    <div className={cn("inline-flex", className)}>
      <Button variant="outline" size="sm" onClick={handleOpen}>
        <span className="relative">
          <Search className="size-4 mr-1.5" />
          {value && (
            <span className="absolute -top-1 -right-0.5 size-2 rounded-full bg-primary" />
          )}
        </span>
        Search
      </Button>
    </div>
  )
}
